import client from '@lib/sanity';
import { createImageUrlBuilder } from '@sanity/image-url';
import Img, { ImageProps } from 'next/image';
import React, { useState, useRef, useEffect } from 'react';

const builder = createImageUrlBuilder(client);

interface IImage {
  sanityImage: {
    asset: {
      _ref: string,
      _type: string,
    },
    crop?: {
      _type: string,
      top: number,
      bottom: number,
      left: number,
      right: number,
    },
    hotspot?: {
      _type: string,
      x: number,
      y: number,
      height: number,
      width: number,
    },
    _type: string,
    alt: string,
  }
  className?: string,
  layout?: 'intrinsic' | 'responsive' | 'fill' | 'fixed',
  priority?: ImageProps['priority'],
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down',
  width?: ImageProps['width'],
  height?: ImageProps['height'],
  sizes?: ImageProps['sizes'],
}

const styling = {
  image: 'w-full h-auto',
};

const defaultMaxWidth = 1440;
const defaultMaxHeight = 960;
const imageQuality = 85;

interface IDimensions {
  width: number,
  height: number,
  aspectRatio: number,
}

// Parse the original asset dimensions from a Sanity image ref.
// Ref format: image-{assetId}-{width}x{height}-{format}
const getOriginalDimensions = (ref: string): IDimensions => {
  const dimensions = ref.split('-')[2] || '';
  const [ width, height ] = dimensions.split('x').map(Number);
  const safeWidth = width || defaultMaxWidth;
  const safeHeight = height || defaultMaxHeight;
  return { width: safeWidth, height: safeHeight, aspectRatio: safeWidth / safeHeight };
};

// Apply the crop rectangle (if present) to get the effective dimensions,
// mirroring what next-sanity-image exposed as `croppedImageDimensions`.
const getCroppedDimensions = (sanityImage: IImage['sanityImage'], original: IDimensions): IDimensions => {
  const crop = sanityImage.crop;
  if (!crop) {
    return original;
  }
  const width = original.width * (1 - (crop.left || 0) - (crop.right || 0));
  const height = original.height * (1 - (crop.top || 0) - (crop.bottom || 0));
  return { width, height, aspectRatio: width / height };
};

export const Image = ({
  sanityImage,
  width,
  height,
  layout = 'intrinsic',
  objectFit = 'cover',
  className = '',
  priority = false,
  sizes,
}: IImage) => {
  const ref = useRef<HTMLDivElement>(null);
  const [ elementWidth, setElementWidth ] = useState(width ? Math.round(Number(width)) : defaultMaxWidth);
  const [ elementHeight, setElementHeight ] = useState(height ? Math.round(Number(height)) : defaultMaxHeight);

  useEffect(() => {
    // Only update dimensions if they're smaller than current
    const imgElement = (ref.current?.firstChild as HTMLElement);
    if (imgElement) {
      const newWidth = imgElement.offsetWidth;
      const newHeight = imgElement.offsetHeight;

      if (newWidth && newWidth < elementWidth) {
        setElementWidth(Math.round(newWidth));
      }
      if (newHeight && newHeight < elementHeight) {
        setElementHeight(Math.round(newHeight));
      }
    }
  }, [ elementWidth, elementHeight ]);

  // Build image props with dynamic sizing (replaces next-sanity-image's useNextSanityImage).
  const getImageProps = (): { src: string, width: number, height: number } => {
    try {
      const original = getOriginalDimensions(sanityImage.asset._ref);
      const cropped = getCroppedDimensions(sanityImage, original);
      const ratio = cropped.aspectRatio || 1;

      if (layout === 'fill') {
        // For carousel/fill layouts, use a larger width to maintain quality.
        const src = builder.image(sanityImage).width(defaultMaxWidth).quality(imageQuality).fit('clip').url();
        return { src, width: defaultMaxWidth, height: Math.round(defaultMaxWidth / ratio) };
      }

      const neededWidth = objectFit === 'contain' ? 0 : elementHeight * ratio;
      const calculatedWidth = Math.round(Math.max(elementWidth, neededWidth));
      const targetWidth = Math.max(1, Math.round(Math.min(
        Math.floor(cropped.width || defaultMaxWidth),
        calculatedWidth,
        defaultMaxWidth
      )));

      const src = builder.image(sanityImage).width(targetWidth).quality(imageQuality).fit('clip').url();
      return { src, width: targetWidth, height: Math.round(targetWidth / ratio) };
    } catch (error) {
      console.error('Error in getImageProps:', error);
      const src = builder.image(sanityImage).width(2048).quality(imageQuality).fit('clip').url();
      return { src, width: 2048, height: Math.round(2048 / (defaultMaxWidth / defaultMaxHeight)) };
    }
  };

  const imageProps = getImageProps();

  // Handle hotspot positioning and get Sanity props
  const getSanityProps = () => {
    let style = { objectPosition: 'center' };
    if (sanityImage?.hotspot && objectFit !== 'contain') {
      const { x = 0.5, y = 0.5 } = sanityImage.hotspot;
      const left = x * 100;
      const top = y * 100;
      style = { objectPosition: `${ left }% ${ top }%` };
    }

    const { width: imgWidth, height: imgHeight, src } = imageProps;

    // Ensure width and height are valid integers
    const safeWidth = typeof imgWidth === 'number' ? Math.max(1, Math.round(imgWidth)) : defaultMaxWidth;
    const safeHeight = typeof imgHeight === 'number' ? Math.max(1, Math.round(imgHeight)) : defaultMaxHeight;

    const baseProps = {
      style,
      src,
      width: safeWidth,
      height: safeHeight,
    };

    if (layout === 'fill') {
      return {
        ...baseProps,
        width: undefined,
        height: undefined,
      };
    }

    return baseProps;
  };

  const loader = ({ src, width, height, quality }: {
    src: string,
    width?: number | string,
    height?: number | string,
    quality?: number | string
  }): string => {
    try {
      const url = new URL(src);
      // Convert all parameters to valid integers
      const safeWidth = width ? Math.max(1, Math.round(Number(width))) : undefined;
      const safeHeight = height ? Math.max(1, Math.round(Number(height))) : undefined;
      const safeQuality = quality ? Math.min(100, Math.max(1, Math.round(Number(quality)))) : undefined;

      if (safeWidth && !isNaN(safeWidth)) {
        url.searchParams.set('w', safeWidth.toString());
        // Add blur effect for small images (thumbnails/placeholders)
        if (safeWidth <= 40) {
          url.searchParams.set('blur', '20');
          url.searchParams.set('q', '30');
        }
      }
      if (safeHeight && !isNaN(safeHeight)) {
        url.searchParams.set('h', safeHeight.toString());
      }
      if (safeQuality && !isNaN(safeQuality)) {
        url.searchParams.set('q', safeQuality.toString());
      }

      const finalUrl = url.toString();
      return finalUrl;
    } catch (error) {
      console.error('Error in loader:', error, { src, width, height, quality });
      return src;
    }
  };

  // Get wrapper class based on layout
  const wrapperClass = layout === 'fill'
    ? 'relative w-full h-full'
    : layout === 'responsive'
      ? 'block'
      : '';

  if (!sanityImage?.asset?._ref) {
    return (
      <div
        className={ `${ wrapperClass } bg-shade-200 min-h-[200px]` }
        role='img'
        aria-label={ sanityImage?.alt || 'Missing image' }
      />
    );
  }

  return (
    <div ref={ ref } className={ wrapperClass }>
      <Img
        { ...getSanityProps() }
        alt={ sanityImage?.alt || 'Missing image' }
        className={ `${ styling.image } ${ className } object-${ objectFit }` }
        fill={ layout === 'fill' }
        priority={ priority }
        sizes={ sizes || `(max-width: 768px) 100vw,
          (max-width: 1200px) 50vw,
          ${ defaultMaxWidth }px` }
        loader={ loader }
        unoptimized={ true } // This is already optimized by Sanity
      />
    </div>
  );
};

export default Image;
